import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import apiClient from '../../lib/api';

const { FiX } = FiIcons;

// Add a company to the pipeline by hand (the agent uses the create_crm_account tool
// / POST /crm/accounts instead). Only `company` is required.
const AddAccountModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    company: '', amount: '', series: '', leadInvestor: '',
    coverageUrl: '', description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.company.trim()) { setError('Company name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await apiClient.createCrmAccount({
        company: form.company.trim(),
        amount: form.amount ? Number(form.amount) : undefined,
        series: form.series || undefined,
        leadInvestor: form.leadInvestor || undefined,
        coverageUrl: form.coverageUrl || undefined,
        description: form.description || undefined,
        source: 'manual',
      });
      onCreated();
    } catch (err) {
      setError(err.message || 'Failed to create account');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-md"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Add Company</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
          <Field label="Company *">
            <input value={form.company} onChange={set('company')} className={inputCls} autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount ($)">
              <input value={form.amount} onChange={set('amount')} type="number" className={inputCls} placeholder="50000000" />
            </Field>
            <Field label="Series / Round">
              <input value={form.series} onChange={set('series')} className={inputCls} placeholder="Series A" />
            </Field>
          </div>
          <Field label="Lead Investor">
            <input value={form.leadInvestor} onChange={set('leadInvestor')} className={inputCls} />
          </Field>
          <Field label="Coverage URL">
            <input value={form.coverageUrl} onChange={set('coverageUrl')} className={inputCls} placeholder="https://…" />
          </Field>
          <Field label="Description">
            <textarea value={form.description} onChange={set('description')} rows={3} className={inputCls} />
          </Field>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-900">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Add Company'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

export default AddAccountModal;
